import { useEffect } from 'react';
import { Routes, Route, Navigate, useParams, Outlet } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { useApp }  from './context/AppContext';
import Nav         from './components/Nav';

import Login        from './pages/Login';
import Signup       from './pages/Signup';
import ConfirmEmail from './pages/ConfirmEmail';
import Leagues      from './pages/Leagues';
import Dashboard    from './pages/Dashboard';
import Players      from './pages/Players';
import Session      from './pages/Session';
import LeagueSettings from './pages/LeagueSettings';
import History      from './pages/History';
import Account      from './pages/Account';
import Kiosk        from './pages/Kiosk';
import KioskSession from './pages/KioskSession';

/**
 * Reads :leagueId from the URL and tells AppContext which league to load.
 * Wraps all /leagues/:leagueId/* routes.
 */
function LeagueLayout() {
  const { leagueId }   = useParams<{ leagueId: string }>();
  const { switchLeague } = useApp();

  useEffect(() => {
    if (leagueId) switchLeague(leagueId);
  }, [leagueId, switchLeague]);

  return (
    <>
      <Nav />
      <Outlet />
    </>
  );
}

export default function App() {
  const { organizer, isLoading } = useAuth();

  // Kiosk is always public — render before auth check
  return (
    <Routes>
      {/* ── Public kiosk routes ──────────────────────────────────────────── */}
      <Route path="/kiosk/:leagueId/register" element={<Kiosk mode="register" />} />
      <Route path="/kiosk/:leagueId/scores"   element={<Kiosk mode="scores"   />} />
      <Route path="/kiosk/:leagueId/session"  element={<KioskSession />}          />

      {/* ── Auth routes ───────────────────────────────────────────────────── */}
      <Route path="/login"         element={<Login />} />
      <Route path="/signup"        element={<Signup />} />
      <Route path="/confirm-email" element={<ConfirmEmail />} />

      {/* ── Loading spinner while restoring session ───────────────────────── */}
      {isLoading && (
        <Route path="*" element={
          <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <p style={{ color: 'var(--gray-400)' }}>Loading…</p>
          </div>
        } />
      )}

      {/* ── Authenticated routes ──────────────────────────────────────────── */}
      {!isLoading && organizer && (
        <>
          <Route path="/account" element={<><Nav /><Account /></>} />
          <Route path="/leagues" element={<Leagues />} />
          <Route path="/leagues/:leagueId" element={<LeagueLayout />}>
            <Route index                element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard"    element={<Dashboard />} />
            <Route path="players"      element={<Players />} />
            <Route path="session"      element={<Session />} />
            <Route path="settings"     element={<LeagueSettings />} />
            <Route path="history"      element={<History />} />
            <Route path="*"            element={<Navigate to="dashboard" replace />} />
          </Route>
          <Route path="*" element={<Navigate to="/leagues" replace />} />
        </>
      )}

      {/* ── Unauthenticated fallback ──────────────────────────────────────── */}
      {!isLoading && !organizer && (
        <Route path="*" element={<Navigate to="/login" replace />} />
      )}
    </Routes>
  );
}
